import type { ReactNode } from "react";
import Constants from "@-/com.npmjs.expo-constants";
import onetime from "@-/com.npmjs.onetime";
import { Platform } from "@-/com.npmjs.react-native";
import HeadFromExpoRouter from "expo-router/head";

/**
 * `expo-router/head` is two different things:
 * - on web it is react-helmet: `<title>`/`<meta>` children land in the document head (and in the
 *   server-rendered HTML);
 * - on iOS (`ExpoHead.ios.js`, active in every bare/dev-client build because the `ExpoHead` native
 *   module is present) it donates an `NSUserActivity` for the focused route — Handoff / Spotlight.
 *   To build that activity's `webpageURL` it reads `extra.router.origin` (or `headOrigin` /
 *   `generatedOrigin`) from the app config and, when none is set, `throw`s in development
 *   ("Expo Head: Add the handoff origin to the Expo Config (requires rebuild)…") or `alert`s in
 *   production. That is a fatal render error for ANY screen that renders `<Head>` on native.
 *
 * Han does not configure an origin (it does not use Handoff), so on native `Head` renders nothing
 * unless an origin is configured — at which point the real Handoff implementation takes over
 * unchanged. Call sites can therefore render `<Head>` unconditionally on every platform.
 */
const isHeadEnabled = onetime((): boolean => {
  if (Platform.OS === "web") {
    return true;
  }
  const router: unknown = Constants.expoConfig?.extra?.router;
  if (typeof router !== "object" || router === null) {
    return false;
  }
  const { generatedOrigin, headOrigin, origin } = router as Record<string, unknown>;
  return [headOrigin, origin, generatedOrigin].some(
    (value) => typeof value === "string" && value !== "",
  );
});

export function Head({ children }: { children?: ReactNode }) {
  if (!isHeadEnabled()) {
    return null;
  }
  return <HeadFromExpoRouter>{children}</HeadFromExpoRouter>;
}
Head.Provider = HeadFromExpoRouter.Provider;
